if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.defaultSubmitButtonText = this.submitButtonText.textContent.trim();
        this.buttonsWrapper = this.querySelector('.product-form__buttons');
        this.ctaPlaceholder = this.querySelector('.product-form__cta-placeholder');
        this.ctaPlaceholderText = this.ctaPlaceholder?.querySelector('span');
        this.backInStockContainer = this.querySelector('#backinstock');
        this.isVariantUpdating = false;
        this.expectedCtaMode = this.submitButton.dataset.selectionState === 'sold-out' ? 'back-in-stock' : 'add-to-cart';
        this.pendingUnavailableText = '';

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.backInStockContainer) {
          this.backInStockObserver = new MutationObserver(() => this.updateCtaMode());
          this.backInStockObserver.observe(this.backInStockContainer, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this.updateCtaMode();
      }

      disconnectedCallback() {
        this.backInStockObserver?.disconnect();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || !this.variantIdInput.value) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      setSubmitButtonUpdating(isUpdating = true) {
        this.isVariantUpdating = isUpdating;
        if (isUpdating) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.setAttribute('data-variant-updating', 'true');
        } else {
          this.submitButton.removeAttribute('data-variant-updating');
          if (!this.submitButton.hasAttribute('disabled')) {
            this.submitButton.removeAttribute('aria-disabled');
          }
        }
        this.updateCtaMode();
      }

      toggleSubmitButton(disable = true, text) {
        this.setSelectionState({ state: disable ? 'sold-out' : 'available', text });
      }

      setSelectionState({ state, text }) {
        const disable = state !== 'available';
        this.submitButton.dataset.selectionState = state;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.removeAttribute('data-variant-updating');
          this.isVariantUpdating = false;
          this.expectedCtaMode = state === 'sold-out' ? 'back-in-stock' : 'add-to-cart';
          this.pendingUnavailableText = text || window.variantStrings.soldOut;
          this.updatePlaceholderText(this.pendingUnavailableText);
          this.submitButtonText.textContent = this.pendingUnavailableText;

          if (state === 'sold-out' && this.backInStockContainer) {
            this.submitButton.setAttribute('data-awaiting-back-in-stock', 'true');
          } else {
            this.submitButton.removeAttribute('data-awaiting-back-in-stock');
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.removeAttribute('data-variant-updating');
          this.submitButton.removeAttribute('data-awaiting-back-in-stock');
          this.isVariantUpdating = false;
          this.expectedCtaMode = 'add-to-cart';
          this.pendingUnavailableText = '';
          this.updatePlaceholderText(window.variantStrings.addToCart || this.defaultSubmitButtonText);
          this.submitButtonText.textContent = window.variantStrings.addToCart || this.defaultSubmitButtonText;
        }
        this.updateCtaMode();
      }

      updateCtaMode() {
        if (!this.buttonsWrapper) return;

        const hasBackInStockContent = this.hasBackInStockContent();
        let ctaMode = this.buttonsWrapper.dataset.ctaMode || 'add-to-cart';

        if (this.isVariantUpdating) {
          this.buttonsWrapper.dataset.ctaMode = ctaMode;
          return;
        }

        if (this.expectedCtaMode === 'back-in-stock') {
          if (hasBackInStockContent) {
            ctaMode = 'back-in-stock';
            this.submitButton.removeAttribute('data-awaiting-back-in-stock');
          } else {
            ctaMode = 'back-in-stock-pending';
          }
        } else {
          ctaMode = 'add-to-cart';
          this.submitButton.removeAttribute('data-awaiting-back-in-stock');
        }

        this.buttonsWrapper.dataset.ctaMode = ctaMode;
      }

      updatePlaceholderText(text) {
        if (!this.ctaPlaceholderText || !text) return;

        this.ctaPlaceholderText.textContent = text;
      }

      hasBackInStockContent() {
        if (!this.backInStockContainer) return false;

        return (
          this.backInStockContainer.childElementCount > 0 ||
          this.backInStockContainer.textContent.trim().length > 0
        );
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
